/**
 * EmbedEntry — a porta de entrada do shell embutido (EMB-08/EMB-10/EMB-14)
 *
 * Fica ACIMA de InternalLayout na árvore de /embed (App.jsx) e decide o que
 * renderiza antes de qualquer tela de conta:
 *
 *   busca a allowlist de origens ──→ tela de espera (nada é confiável ainda)
 *   sem sessão ainda (bootstrap) ─┐
 *   sessão expirou (auth:expired) ┴─→ pede um token ao host (EmbedContext,
 *                                      T12) e mostra a tela de espera
 *   token chega a tempo           ──→ re-roda checkAuth() e renderiza children
 *   host nunca responde           ──→ EmbedExpired (nunca a tela de login)
 *
 * O PRIMEIRO token do bootstrap usa o MESMO protocolo de uma renovação —
 * "sem token" e "token expirado" pedem a mesma coisa ao host, então não há
 * um segundo caminho a manter.
 *
 * A allowlist vem de `GET /api/embed/config` (público, sem sessão — o shell
 * precisa saber de quem aceitar um token ANTES de ter um), alimentado pelo
 * SSM `platform/embed-allowed-origins`. A renovação só é pedida DEPOIS que
 * ela chega: pedir antes abriria uma janela em que a resposta do host chega
 * e é descartada, queimando o timeout de 5s por uma corrida. Qualquer falha
 * resolve para lista vazia — fail closed, nunca "confia em todo mundo".
 */
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { EmbedProvider, useEmbed } from "@/contexts/EmbedContext";
import BrandLoader from "@/components/BrandLoader";
import EmbedExpired from "./EmbedExpired";

const LANDING_LABEL = "PREPARANDO SEU ATENDIMENTO";

function EmbedGate({ children }) {
  const { renewalFailed, requestRenewal } = useEmbed();
  const { isAuthenticated, isLoading } = useAuth();
  const requestedInitialToken = useRef(false);

  useEffect(() => {
    if (isLoading || isAuthenticated || requestedInitialToken.current) return;
    requestedInitialToken.current = true;
    requestRenewal();
  }, [isLoading, isAuthenticated, requestRenewal]);

  if (renewalFailed) return <EmbedExpired />;

  // Tela de pouso — nunca a tela de login padrão, que este usuário não tem
  // senha para preencher (context.md decisão 6).
  if (!isAuthenticated) {
    return <BrandLoader fullscreen label={LANDING_LABEL} />;
  }

  return children;
}

export default function EmbedEntry({ children }) {
  const { checkAuth } = useAuth();
  const [allowedOrigins, setAllowedOrigins] = useState(null); // null = ainda buscando

  useEffect(() => {
    let cancelled = false;

    fetch("/api/embed/config")
      .then((res) => (res.ok ? res.json() : { allowedOrigins: [] }))
      .then((data) => {
        if (cancelled) return;
        setAllowedOrigins(Array.isArray(data?.allowedOrigins) ? data.allowedOrigins : []);
      })
      .catch(() => {
        if (!cancelled) setAllowedOrigins([]); // fail closed
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Sem a allowlist não dá para montar o provider ainda: o EmbedGate pediria
  // o token de imediato e a resposta do host chegaria numa janela em que
  // nenhuma origem é confiável.
  if (allowedOrigins === null) {
    return <BrandLoader fullscreen label={LANDING_LABEL} />;
  }

  return (
    <EmbedProvider isEmbed allowedOrigins={allowedOrigins} onToken={checkAuth}>
      <EmbedGate>{children}</EmbedGate>
    </EmbedProvider>
  );
}
